/**
 * Typed wrappers for every API route the web app uses. Shapes mirror the API's route
 * handlers; change one, change the other.
 */

import { api, apiRaw, apiStream, buildUrl } from './client.ts';

// ---------------------------------------------------------------- site & auth

export type SessionStage = 'active' | 'totp_required' | 'totp_enrol' | 'password_change';
export type OnboardingMode = 'off' | 'open' | 'invite' | 'approval';

export interface TenantBranding {
  displayName?: string;
  logoUrl?: string;
  primaryColor?: string;
  loginHeading?: string;
  loginSubheading?: string;
  loginFooter?: string;
  supportEmail?: string;
  /** One of the shipped pictures by id, or this class's own uploaded one. */
  loginBackground?: string;
  /** How hard that picture is pushed back, so the words on top stay readable. */
  loginOverlay?: 'soft' | 'medium' | 'strong';
}

export interface PublicTenant {
  slug: string;
  name: string;
  status: 'active' | 'suspended' | 'banned';
  onboardingMode: OnboardingMode;
  branding: TenantBranding;
}

/** What the platform is telling everybody today, if anything. */
export interface Announcement {
  message: string;
  tone: 'info' | 'warning' | 'maintenance';
  until: string | null;
}

export type SiteInfo =
  | {
      kind: 'tenant';
      tenant: PublicTenant;
      turnstileSiteKey: string | null;
      announcement?: Announcement | null;
    }
  | { kind: 'platform'; turnstileSiteKey: string | null; announcement?: Announcement | null };

export interface PublicUser {
  id: string;
  username: string;
  displayName: string;
  role: 'student' | 'tenant_admin';
  email: string | null;
}

export type SessionInfo =
  | { authenticated: false; tenant: PublicTenant }
  | {
      authenticated: true;
      stage: SessionStage;
      csrfToken: string;
      user: PublicUser;
      tenant: PublicTenant;
      /** Set while somebody from Praxadon support is looking, with permission. */
      support?: { reason: string; until: string };
    };

export const site = {
  get: () => api<SiteInfo>('GET', '/api/public/site'),
};

export const auth = {
  session: () => api<SessionInfo>('GET', '/api/auth/session'),
  login: (body: { username: string; password: string; turnstileToken?: string | undefined }) =>
    api<{ stage: SessionStage; csrfToken: string; user: PublicUser }>('POST', '/api/auth/login', {
      body,
    }),
  verifyTotp: (code: string) =>
    api<{ stage: SessionStage; csrfToken: string; recoveryCodesRemaining?: number }>(
      'POST',
      '/api/auth/totp/verify',
      { body: { code } },
    ),
  enrolTotp: () =>
    api<{ uri: string; secret: string }>('POST', '/api/auth/totp/enrol', { body: {} }),
  confirmTotp: (code: string) =>
    api<{ stage: SessionStage; csrfToken: string; recoveryCodes: string[] }>(
      'POST',
      '/api/auth/totp/confirm',
      { body: { code } },
    ),
  logout: () => api<{ ok: true }>('POST', '/api/auth/logout', { body: {} }),
  logoutEverywhere: () => api<{ ok: true }>('POST', '/api/auth/logout-all', { body: {} }),
  forgot: (body: { identifier: string; turnstileToken?: string | undefined }) =>
    api<{ ok: true; message?: string }>('POST', '/api/auth/password/forgot', { body }),
  reset: (body: { token: string; password: string }) =>
    api<{ ok: true; message: string }>('POST', '/api/auth/password/reset', { body }),
  changePassword: (body: { currentPassword: string; newPassword: string }) =>
    api<{ stage: 'active'; csrfToken: string }>('POST', '/api/auth/password/change', { body }),
};

export type UsernameCheck =
  | { available: true; username: string }
  | { available: false; message: string; suggestions?: string[] };

/** One of the extra questions a class asks on its sign-up form. */
export interface SignupQuestion {
  id: string;
  label: string;
  kind: 'text' | 'choice';
  options?: string[];
  required?: boolean;
}

/** Support, only ever with the person's own permission. */
export const support = {
  pending: () =>
    api<{
      pending: { id: string; reason: string; expiresAt: string; consented: boolean } | null;
    }>('GET', '/api/auth/support'),
  answer: (id: string, agreed: boolean) =>
    api<undefined>('POST', `/api/auth/support/${encodeURIComponent(id)}`, { body: { agreed } }),
  claim: (ticket: string) =>
    api<{ ok: true }>('POST', '/api/auth/support/claim', { body: { ticket } }),
};

export const onboarding = {
  form: () => api<{ questions: SignupQuestion[] }>('GET', '/api/onboarding/form'),
  checkUsername: (value: string, signal?: AbortSignal) =>
    api<UsernameCheck>('GET', '/api/onboarding/username', {
      query: { value },
      ...(signal ? { signal } : {}),
    }),
  signup: (body: {
    username: string;
    displayName: string;
    password: string;
    email?: string;
    inviteCode?: string;
    answers?: Record<string, string>;
    turnstileToken?: string | undefined;
  }) =>
    api<
      | { status: 'active'; stage: 'active'; csrfToken: string; user: PublicUser }
      | { status: 'pending'; message: string }
    >('POST', '/api/onboarding/signup', { body }),
  invite: (token: string) =>
    api<{ email: string; suggestedUsername: string | null; role: string; tenantName: string }>(
      'GET',
      '/api/onboarding/invite',
      { query: { token } },
    ),
  acceptInvite: (body: {
    token: string;
    username: string;
    displayName: string;
    password: string;
  }) =>
    api<{ stage: SessionStage; csrfToken: string; user: PublicUser }>(
      'POST',
      '/api/onboarding/invite/accept',
      { body },
    ),
};

// ---------------------------------------------------------------- workspace

export interface Project {
  id: string;
  name: string;
  slug: string;
  template: string;
  entryFile: string;
  storageUsedBytes: number;
  fileCount: number;
  createdAt: string;
  lastEditedAt: string | null;
  publishedAt: string | null;
}

/** Where a project's draft can be previewed: a signed, short-lived link on the student's site. */
export interface PreviewLink {
  /** The student's site, e.g. `https://nick.acme.ide.praxadon.com`. */
  origin: string;
  /** The draft's base address; page paths are appended to it. */
  url: string;
  entryFile: string;
  expiresAt: string;
}

export interface WorkspaceInfo {
  usage: { usedBytes: number; quotaBytes: number; items: number; maxItems: number };
  projects: Project[];
  templates: { id: string; label: string; description: string }[];
  limits: {
    maxFileBytes: number;
    maxEditableBytes: number;
    maxUploadBytes: number;
    maxFilesPerUpload: number;
  };
}

export interface TreeEntry {
  name: string;
  path: string;
  kind: 'file' | 'directory';
  size: number;
  modifiedAt: string;
  hasChildren?: boolean;
  children?: TreeEntry[];
}

/** One entry in a project's flat listing: every file and folder, path and all. */
export interface FlatEntry {
  path: string;
  kind: 'file' | 'directory';
  size: number;
  modifiedAt: string;
}

export interface TextFile {
  path: string;
  content: string;
  etag: string;
  size: number;
  modifiedAt: string;
}

export interface TrashItem {
  id: string;
  kind: 'file' | 'directory' | 'project';
  name: string;
  projectId: string;
  projectName: string | null;
  path: string;
  bytes: number;
  items: number;
  deletedAt: string;
  expiresAt: string;
}

const project = (id: string) => `/api/projects/${encodeURIComponent(id)}`;

export const workspace = {
  info: () => api<WorkspaceInfo>('GET', '/api/workspace'),
  createProject: (body: { name: string; template: string }) =>
    api<{ project: Project }>('POST', '/api/projects', { body }),
  renameProject: (id: string, name: string) =>
    api<{ project: Project }>('PATCH', project(id), { body: { name } }),
  trashProject: (id: string) => api<{ trashed: TrashItem }>('DELETE', project(id)),

  tree: (id: string, path = '', depth = 1) =>
    api<{ entries: TreeEntry[] }>('GET', `${project(id)}/tree`, { query: { path, depth } }),
  readText: (id: string, path: string, signal?: AbortSignal) =>
    api<TextFile>('GET', `${project(id)}/file`, { query: { path }, ...(signal ? { signal } : {}) }),
  listFiles: (id: string) => api<{ files: FlatEntry[] }>('GET', `${project(id)}/files`),
  readAllTexts: (id: string) =>
    api<{ files: TextFile[]; truncated: boolean }>('GET', `${project(id)}/texts`),
  save: (
    id: string,
    body: { path: string; content: string; etag: string | null; force?: boolean },
  ) =>
    api<{ path: string; etag: string; size: number; created: boolean }>(
      'PUT',
      `${project(id)}/file`,
      {
        body,
      },
    ),
  create: (id: string, body: { path: string; kind: 'file' | 'directory'; content?: string }) =>
    api<{ path: string; kind: 'file' | 'directory'; etag?: string }>(
      'POST',
      `${project(id)}/entries`,
      {
        body,
      },
    ),
  move: (id: string, from: string, to: string) =>
    api<{ path: string }>('POST', `${project(id)}/move`, { body: { from, to } }),
  copy: (id: string, from: string, to: string) =>
    api<{ path: string }>('POST', `${project(id)}/copy`, { body: { from, to } }),
  trash: (id: string, path: string) =>
    api<{ trashed: TrashItem }>('POST', `${project(id)}/trash`, { body: { path } }),
  upload: (id: string, form: FormData, query: { dir?: string; onConflict?: string }) =>
    api<{ uploaded: { path: string; size: number; replaced: boolean }[] }>(
      'POST',
      `${project(id)}/upload`,
      { form, query },
    ),
  rawUrl: (id: string, path: string, download = false) =>
    buildUrl(`${project(id)}/raw`, { path, download: download ? 1 : undefined }),
  downloadUrl: (id: string, path = '') =>
    buildUrl(`${project(id)}/download`, { path: path || undefined }),
  raw: (id: string, path: string) => apiRaw(`${project(id)}/raw`, { query: { path } }),

  preview: (id: string) => api<PreviewLink>('POST', `${project(id)}/preview`, { body: {} }),
  publish: (id: string) =>
    api<{ project: Project; url: string }>('POST', `${project(id)}/publish`, { body: {} }),
  unpublish: (id: string) => api<{ project: Project }>('DELETE', `${project(id)}/publish`),

  trashList: () => api<{ items: TrashItem[] }>('GET', '/api/trash'),
  restore: (trashId: string) =>
    api<{ restored: { projectId: string; path: string; kind: TrashItem['kind'] } }>(
      'POST',
      `/api/trash/${encodeURIComponent(trashId)}/restore`,
      { body: {} },
    ),
  purge: (trashId: string) =>
    api<{ purged: number; freedBytes: number }>(
      'DELETE',
      `/api/trash/${encodeURIComponent(trashId)}`,
    ),
  emptyTrash: () => api<{ purged: number; freedBytes: number }>('DELETE', '/api/trash'),
};

// ------------------------------------------------------------- instructors

/** A student as their instructor sees them in the class list. */
export interface ClassStudent {
  id: string;
  username: string;
  displayName: string;
  status: 'pending' | 'active' | 'suspended' | 'banned';
  usedBytes: number;
  quotaBytes: number;
  items: number;
  lastLoginAt: string | null;
}

export interface WorkspaceUsage {
  usedBytes: number;
  quotaBytes: number;
  items: number;
  maxItems: number;
}

/**
 * Reading a student's work. Every route here is read-only and audited on the server;
 * there is deliberately no way from the IDE to change a student's files.
 */
const studentProject = (userId: string, projectId: string) =>
  `/api/instructor/students/${encodeURIComponent(userId)}/projects/${encodeURIComponent(projectId)}`;

export const instructor = {
  students: () => api<{ students: ClassStudent[] }>('GET', '/api/instructor/students'),
  projects: (userId: string) =>
    api<{
      student: { id: string; username: string; displayName: string; status: string };
      usage: WorkspaceUsage;
      projects: Project[];
    }>('GET', `/api/instructor/students/${encodeURIComponent(userId)}/projects`),
  listFiles: (userId: string, projectId: string) =>
    api<{ files: FlatEntry[] }>('GET', `${studentProject(userId, projectId)}/files`),
  readText: (userId: string, projectId: string, path: string, signal?: AbortSignal) =>
    api<TextFile>('GET', `${studentProject(userId, projectId)}/file`, {
      query: { path },
      ...(signal ? { signal } : {}),
    }),
  rawUrl: (userId: string, projectId: string, path: string, download = false) =>
    buildUrl(`${studentProject(userId, projectId)}/raw`, {
      path,
      download: download ? 1 : undefined,
    }),
  downloadUrl: (userId: string, projectId: string, path = '') =>
    buildUrl(`${studentProject(userId, projectId)}/download`, { path: path || undefined }),
};

// ---------------------------------------------------------------- settings

export interface SettingState {
  value: unknown;
  source: 'platform' | 'tenant' | 'user';
  locked: boolean;
  /** For a merged map (`diagnostics.rules`): the class's part and the student's own. */
  inherited?: unknown;
  own?: unknown;
}

export const settings = {
  get: () => api<{ settings: Record<string, SettingState> }>('GET', '/api/settings'),
  save: (values: Record<string, unknown>) =>
    api<{ settings: Record<string, SettingState> }>('PUT', '/api/settings', { body: { values } }),
};

// ---------------------------------------------------------------------- AI

export type AiProviderKind = 'openai' | 'ollama_cloud' | 'ollama_local';

/** A class's provider, as the API shows it: the key itself never comes back. */
export interface AiProvider {
  id: string;
  kind: AiProviderKind;
  label: string;
  apiKeyLast4: string | null;
  baseUrl: string | null;
  defaultModel: string;
  enabled: boolean;
  priority: number;
  lastCheckedAt: string | null;
  lastCheckOk: boolean | null;
  lastCheckError: string | null;
  rotatedAt: string | null;
}

export interface AiUsage {
  since: string;
  budget: {
    monthlyTokenBudget: number;
    monthlyTokensUsed: number;
    monthlyCostCapMicroUsd: number | null;
    monthlyCostMicroUsd: number;
  };
  byFeature: {
    feature: string;
    calls: number;
    refused: number;
    tokens: number;
    costMicroUsd: number;
  }[];
}

export interface AiStatus {
  features: Record<string, { available: boolean; reason?: string }>;
  dailyTokensLeft: number | null;
}

const provider = (id: string) => `/api/admin/ai/providers/${encodeURIComponent(id)}`;

export interface AiThreadSummary {
  id: string;
  title: string | null;
  contextPath: string | null;
  updatedAt: string;
}

export interface AiTurn {
  id: string;
  role: string;
  content: string;
  model: string | null;
  createdAt: string;
}

export interface AskFile {
  path: string;
  content: string;
}

const stream = (path: string, body: unknown, signal?: AbortSignal) =>
  apiStream(path, { body, ...(signal ? { signal } : {}) });

export const ai = {
  status: () => api<AiStatus>('GET', '/api/ai/status'),
  /** The mentor's answer as a stream; read it with `readEvents` (sse.ts). */
  ask: (
    body: { question: string; files?: AskFile[]; threadId?: string; path?: string },
    signal?: AbortSignal,
  ) => stream('/api/ai/ask', body, signal),
  /** What a problem the checker found means, and how to fix it. */
  explainError: (
    body: {
      path: string;
      line: number;
      column?: number;
      message: string;
      ruleId?: string;
      snippet?: string;
      files?: AskFile[];
      threadId?: string;
    },
    signal?: AbortSignal,
  ) => stream('/api/ai/explain-error', body, signal),
  /** What the selected code does. */
  explainCode: (
    body: {
      path: string;
      code: string;
      startLine?: number;
      endLine?: number;
      threadId?: string;
    },
    signal?: AbortSignal,
  ) => stream('/api/ai/explain-code', body, signal),
  /** Why the page is not doing what the student expected. */
  why: (
    body: {
      note?: string;
      files?: AskFile[];
      consoleErrors?: string[];
      problems?: string[];
      threadId?: string;
    },
    signal?: AbortSignal,
  ) => stream('/api/ai/why', body, signal),
  /**
   * The next few characters, for ghost text. 204 (no suggestion) comes back as null —
   * silence is the normal answer, not a failure.
   */
  suggest: async (
    body: { path: string; before: string; after?: string },
    signal?: AbortSignal,
  ): Promise<string | null> => {
    const response = await api<{ text: string } | undefined>('POST', '/api/ai/suggest', {
      body,
      ...(signal ? { signal } : {}),
    });
    return response?.text ?? null;
  },
  threads: () => api<{ threads: AiThreadSummary[] }>('GET', '/api/ai/threads'),
  thread: (id: string) =>
    api<{ thread: AiThreadSummary; turns: AiTurn[] }>(
      'GET',
      `/api/ai/threads/${encodeURIComponent(id)}`,
    ),
  removeThread: (id: string) =>
    api<undefined>('DELETE', `/api/ai/threads/${encodeURIComponent(id)}`),
  providers: () =>
    api<{ providers: AiProvider[]; kinds: AiProviderKind[] }>('GET', '/api/admin/ai/providers'),
  addProvider: (body: {
    kind: AiProviderKind;
    label: string;
    apiKey?: string;
    baseUrl?: string | null;
    defaultModel: string;
  }) => api<{ provider: AiProvider }>('POST', '/api/admin/ai/providers', { body }),
  updateProvider: (
    id: string,
    body: Partial<Pick<AiProvider, 'label' | 'baseUrl' | 'defaultModel' | 'enabled'>> & {
      first?: true;
    },
  ) => api<{ provider: AiProvider }>('PATCH', provider(id), { body }),
  replaceKey: (id: string, apiKey: string) =>
    api<{ provider: AiProvider }>('PUT', `${provider(id)}/key`, { body: { apiKey } }),
  removeProvider: (id: string) => api<undefined>('DELETE', provider(id)),
  testProvider: (id: string) =>
    api<{ check: { ok: boolean; message: string | null; models: string[] }; provider: AiProvider }>(
      'POST',
      `${provider(id)}/test`,
    ),
  usage: () => api<AiUsage>('GET', '/api/admin/ai/usage'),
};

// ---------------------------------------------------------------------------
// Lessons and progress
// ---------------------------------------------------------------------------

export interface LessonStepView {
  id: string;
  title: string;
  instructionsMd: string;
  starterFiles: { path: string; content: string }[];
  hintsMd: string[];
  checkedBy: 'deterministic' | 'ai' | 'none';
}

export interface LessonView {
  id: string;
  slug: string;
  title: string;
  bodyMd: string;
  conceptKeys: string[];
  estimatedMinutes: number | null;
  steps: LessonStepView[];
}

export interface LessonStepResult {
  pass: boolean;
  feedback: string;
  by: string;
  at: string;
}

export interface LessonProgress {
  lessonId: string;
  stepIndex: number;
  stepResults: Record<string, LessonStepResult>;
  startedAt: string;
  completedAt: string | null;
}

export interface LessonSummary {
  id: string;
  slug: string;
  title: string;
  estimatedMinutes: number | null;
  stepCount: number;
  conceptKeys: string[];
  progress: { stepIndex: number; passed: number; completedAt: string | null } | null;
}

export interface CheckResult {
  pass: boolean;
  feedback: string;
  by: 'deterministic' | 'ai' | 'none';
  undecided: boolean;
  progress: LessonProgress;
  conceptsPractised: string[];
}

export interface ConceptProgress {
  conceptKey: string;
  attempts: number;
  correct: number;
  mastery: number;
  mastered: boolean;
  lastAttemptedAt: string | null;
}

const lesson = (slug: string) => `/api/lessons/${encodeURIComponent(slug)}`;

export const lessons = {
  list: () => api<{ lessons: LessonSummary[] }>('GET', '/api/lessons'),
  read: (slug: string) =>
    api<{ lesson: LessonView; progress: LessonProgress | null }>('GET', lesson(slug)),
  start: (slug: string) => api<{ progress: LessonProgress }>('POST', `${lesson(slug)}/start`),
  check: (slug: string, stepId: string, files: AskFile[]) =>
    api<CheckResult>('POST', `${lesson(slug)}/steps/${encodeURIComponent(stepId)}/check`, {
      body: { files },
    }),
  progress: () => api<{ concepts: ConceptProgress[] }>('GET', '/api/progress'),
};

// ---------------------------------------------------------------------------
// Quizzes and exams
// ---------------------------------------------------------------------------

export type QuizQuestionKind =
  | 'multiple_choice'
  | 'multiple_select'
  | 'fill_in_code'
  | 'fix_the_bug'
  | 'build_to_match'
  | 'short_answer';

/** A question as a student sees it: no answer key, no rubric, no explanation. */
export interface OpenQuestion {
  id: string;
  kind: QuizQuestionKind;
  promptMd: string;
  points: number;
  options?: { id: string; label: string }[];
  starterFiles?: { path: string; content: string }[];
  hintsMd?: string[];
  targetImageUrl?: string;
}

export interface OpenQuiz {
  id: string;
  title: string;
  mode: 'practice' | 'exam';
  timeLimitSeconds: number | null;
  passScorePercent: number;
  questions: OpenQuestion[];
}

export interface QuizAttemptView {
  id: string;
  quizId: string;
  attemptNumber: number;
  status: string;
  answers: Record<string, unknown>;
  startedAt: string;
  expiresAt: string | null;
  submittedAt: string | null;
}

export interface QuizMarkView {
  questionId: string;
  pointsEarned: number;
  pointsPossible: number;
  correct: boolean;
  feedbackMd?: string;
  gradedBy: 'deterministic' | 'ai' | 'instructor';
  unmarked?: boolean;
}

export interface QuizResult {
  attempt: QuizAttemptView;
  marks: QuizMarkView[];
  scorePercent: number;
  pointsEarned: number;
  pointsPossible: number;
  passed: boolean;
  unmarked: number;
  explanations: { questionId: string; explanationMd?: string; correctOptionIds?: string[] }[];
}

export interface QuizSummary {
  id: string;
  title: string;
  mode: 'practice' | 'exam';
  questionCount: number;
  timeLimitSeconds: number | null;
  maxAttempts: number | null;
  passScorePercent: number;
  dueAt: string | null;
  opensAt: string | null;
  attempts: number;
  bestScorePercent: number | null;
  lastStatus: string | null;
}

export type IntegrityKind =
  'tab_blur' | 'paste' | 'devtools' | 'time_expired' | 'navigation' | 'copy';

const attempt = (id: string) => `/api/quizzes/attempts/${encodeURIComponent(id)}`;

export const quizzes = {
  list: () => api<{ quizzes: QuizSummary[] }>('GET', '/api/quizzes'),
  start: (quizId: string) =>
    api<{ quiz: OpenQuiz; attempt: QuizAttemptView }>(
      'POST',
      `/api/quizzes/${encodeURIComponent(quizId)}/attempts`,
    ),
  save: (attemptId: string, answers: Record<string, unknown>) =>
    api<undefined>('PUT', `${attempt(attemptId)}/answers`, { body: { answers } }),
  /** What the browser noticed during an exam. Never blocks the student. */
  report: (
    attemptId: string,
    kind: IntegrityKind,
    meta?: Record<string, string | number | boolean>,
  ) =>
    api<undefined>('POST', `${attempt(attemptId)}/events`, {
      body: { kind, ...(meta ? { meta } : {}) },
    }),
  submit: (attemptId: string) => api<QuizResult>('POST', `${attempt(attemptId)}/submit`),
  result: (attemptId: string) => api<QuizResult & { quiz: OpenQuiz }>('GET', attempt(attemptId)),
};

export interface InstructorQuizRow {
  id: string;
  title: string;
  mode: 'practice' | 'exam';
  questionCount: number;
  published: boolean;
  aiGenerated: boolean;
  timeLimitSeconds: number | null;
  passScorePercent: number;
  maxAttempts: number | null;
  assignedToEveryone: boolean;
  attempts: number;
  updatedAt: string;
}

export interface GradebookRow {
  userId: string;
  username: string;
  displayName: string | null;
  quizzesTaken: number;
  averagePercent: number | null;
  conceptsMastered: number;
  conceptsAttempted: number;
  weakest: string[];
}

export interface AttemptRow {
  id: string;
  userId: string;
  username: string;
  displayName: string | null;
  attemptNumber: number;
  status: string;
  scorePercent: number | null;
  passed: boolean | null;
  unmarked: number;
  integrityEvents: number;
  overridden: boolean;
  submittedAt: string | null;
}

const adminQuiz = (id: string) => `/api/admin/quizzes/${encodeURIComponent(id)}`;

export const assessment = {
  quizzes: () =>
    api<{ quizzes: InstructorQuizRow[]; weakestConcepts: string[] }>('GET', '/api/admin/quizzes'),
  quiz: (id: string) =>
    api<{ quiz: Record<string, unknown>; attempts: AttemptRow[] }>('GET', adminQuiz(id)),
  generate: (body: {
    topic: string;
    lessonId?: string;
    questionCount?: number;
    mode?: 'practice' | 'exam';
    tuneToClass?: boolean;
  }) => api<{ quizId: string; questions: number }>('POST', '/api/admin/quizzes/generate', { body }),
  update: (
    id: string,
    body: {
      title?: string;
      mode?: 'practice' | 'exam';
      timeLimitSeconds?: number | null;
      maxAttempts?: number | null;
      passScorePercent?: number;
      published?: boolean;
    },
  ) => api<undefined>('PATCH', adminQuiz(id), { body }),
  assign: (
    id: string,
    body: { userId?: string | null; opensAt?: string | null; dueAt?: string | null },
  ) => api<undefined>('POST', `${adminQuiz(id)}/assign`, { body }),
  attempt: (attemptId: string) =>
    api<{
      attempt: Record<string, unknown>;
      student: Record<string, unknown>;
      quiz: Record<string, unknown>;
    }>('GET', `/api/admin/quizzes/attempts/${encodeURIComponent(attemptId)}`),
  override: (
    attemptId: string,
    body: {
      marks: { questionId: string; pointsEarned: number; feedbackMd?: string }[];
      reason: string;
    },
  ) =>
    api<{ scorePercent: number; passed: boolean }>(
      'POST',
      `/api/admin/quizzes/attempts/${encodeURIComponent(attemptId)}/override`,
      { body },
    ),
  gradebook: () => api<{ students: GradebookRow[] }>('GET', '/api/admin/gradebook'),
  gradebookCsvUrl: () => '/api/admin/gradebook.csv',
};
